import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
});

// ── Query Keys ────────────────────────────────────────────────────────────────
const KEYS = {
  trackedVehicles: ()    => ["fastag", "vehicles"],
  fastagDetails:   (vn)  => ["fastag", "details", vn],
  fastagFromDB:    (vn)  => ["fastag", "db", vn],
  allFastagFromDB: (state)     => ["fastag", "db", "all", state ?? "all"],
  availableStates: ()          => ["fastag", "states"],
};

// ── Get all tracked vehicles (sidebar list) ───────────────────────────────────
export function useTrackedVehicles() {
  return useQuery({
    queryKey: KEYS.trackedVehicles(),
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await api.get("/api/fleet/fastag/db/vehicles");
      const raw = data?.data || [];
      return raw.map((v) => ({
        vehicleRegNo: v.vehicleRegNo,
        vehicleType:  v.vehicleType || "—",
        vehicleCategory: v.vehicleCategory || null,
        vendorCode:      v.vendorCode      || null,
        vendorName:      v.vendorName      || null,
        addedAt:      v.addedAt     || null,
      }));
    },
  });
}

// ── Fetch latest transactions from ULIP + saves to DB ────────────────────────
export function useFastagDetails(vehicleNumber) {
  return useQuery({
    queryKey: KEYS.fastagDetails(vehicleNumber),
    enabled:  !!vehicleNumber,
    staleTime: 0,
    retry: 1,
    queryFn: async () => {
      const { data } = await api.get(`/api/fleet/fastag/${vehicleNumber}`);
      const raw = data?.data || [];
      return raw
        .map((t) => ({
          vehicleRegNo:     t.vehicleRegNo,
          readerReadTime:   t.readerReadTime   || null,
          seqNo:            t.seqNo,
          laneDirection:    t.laneDirection    || null,
          tollPlazaName:    t.tollPlazaName    || null,
          tollPlazaGeocode: t.tollPlazaGeocode || null,
          vehicleType:      t.vehicleType      || null,
        }))
        .sort((a, b) => new Date(b.readerReadTime) - new Date(a.readerReadTime));
    },
  });
}

// ── Get transactions from DB (fallback when ULIP fails) ───────────────────────
// Accepts options so the caller can pass { enabled: false } to keep it dormant
// until ULIP has actually failed.
export function useFastagFromDB(vehicleNumber, options = {}) {
  return useQuery({
    queryKey: KEYS.fastagFromDB(vehicleNumber),
    // Default: dormant. Caller enables it by passing { enabled: true/condition }.
    enabled: false,
    staleTime: 2 * 60 * 1000,
    retry: 1,
    queryFn: async () => {
      const { data } = await api.get(`/api/fleet/fastag/db/${vehicleNumber}`);
      const raw = data?.data || [];
      return raw
        .map((t) => ({
          vehicleRegNo:     t.vehicleRegNo,
          readerReadTime:   t.readerReadTime   || null,
          seqNo:            t.seqNo,
          laneDirection:    t.laneDirection    || null,
          tollPlazaName:    t.tollPlazaName    || null,
          tollPlazaGeocode: t.tollPlazaGeocode || null,
          vehicleType:      t.vehicleType      || null,
          fetchedAt:        t.fetchedAt        || null,
        }))
        .sort((a, b) => new Date(b.readerReadTime) - new Date(a.readerReadTime));
    },
    // Spread caller options last so { enabled } from the component overrides
    // the default false above.
    ...options,
  });
}

export function useAllFastagFromDB(state) {
  return useQuery({
    queryKey: KEYS.allFastagFromDB(state),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000, // auto-poll every 5 min
    queryFn: async () => {
      const params = state && state !== "all" ? { state } : {};
      const { data } = await api.get("/api/fleet/fastag/db/all", { params });
      const grouped = data?.data || {};
      // Normalise each vehicle's transactions
      return Object.fromEntries(
        Object.entries(grouped).map(([vn, txns]) => [
          vn,
          txns
            .map((t) => ({
              vehicleRegNo:     t.vehicleRegNo,
              readerReadTime:   t.readerReadTime   || null,
              seqNo:            t.seqNo,
              laneDirection:    t.laneDirection    || null,
              tollPlazaName:    t.tollPlazaName    || null,
              tollPlazaGeocode: t.tollPlazaGeocode || null,
              vehicleType:      t.vehicleType      || null,
              tollState:        t.tollState        || null,
              fetchedAt:        t.fetchedAt        || null,
            }))
            .sort((a, b) => new Date(b.readerReadTime) - new Date(a.readerReadTime)),
        ])
      );
    },
  });
}
 
// ── Get distinct states from DB (for state dropdown) ─────────────────────────
// Populated from tollState column which is set by toll-state.json lookup
// on the backend at insert time — no client-side scanning needed.
export function useAvailableStates() {
  return useQuery({
    queryKey: KEYS.availableStates(),
    staleTime: 10 * 60 * 1000, // states don't change often
    queryFn: async () => {
      const { data } = await api.get("/api/fleet/fastag/db/states");
      return data?.data || [];
    },
  });
}

export function useRefreshStateVehicles() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (state) => {
      const { data } = await api.post("/api/fleet/fastag/refresh-state", { state });
      return data;
    },
    onSuccess: (data, state) => {
      const delay = Math.min(8000 + (data.total || 0) * 200, 30000);
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["fastag", "db", "all", state] });
        queryClient.invalidateQueries({ queryKey: ["fastag", "db", "all", "all"] });
      }, delay);
    },
  });
}
 
// ── Trigger background ULIP refresh for all vehicles ─────────────────────────
// Backend responds with 202 immediately and processes via setImmediate.
// Call invalidateQueries after the expected processing time to get fresh data.
export function useRefreshAllVehicles() {
  const queryClient = useQueryClient();
 
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post("/api/fleet/fastag/refresh-all");
      return data;
    },
    onSuccess: (data) => {
      const totalVehicles = data?.total || 0;
      // Wait ~8s (backend uses setImmediate between vehicles) then
      // invalidate so useAllFastagFromDB re-fetches fresh DB data
      const delay = Math.min(8000 + totalVehicles * 200, 30000);
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["fastag", "db", "all"] });
        queryClient.invalidateQueries({ queryKey: KEYS.availableStates() });
        queryClient.invalidateQueries({ queryKey: KEYS.trackedVehicles() });
      }, delay);
    },
    onError: (err) => {
      console.error("refresh-all failed:", err.message);
    },
  });
}

// ── Add a vehicle to tracked list ─────────────────────────────────────────────
export function useAddTrackedVehicle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ vehicleRegNo, vehicleType }) => {
      const { data } = await api.post("/api/fleet/fastag/vehicles", {
        vehicleRegNo,
        vehicleType: vehicleType || null,
      });
      return { txns: data.data, vehicleRegNo };
    },
    onSuccess: ({ txns, vehicleRegNo }) => {
      // Seed the fastag details cache so useFastagDetails won't re-fetch
      queryClient.setQueryData(KEYS.fastagDetails(vehicleRegNo), txns);

      // Add to tracked vehicles list using first txn's vehicleType if available
      queryClient.setQueryData(KEYS.trackedVehicles(), (prev = []) => {
        const alreadyTracked = prev.some((v) => v.vehicleRegNo === vehicleRegNo);
        if (alreadyTracked) return prev;
        return [
          {
            vehicleRegNo,
            vehicleType: txns[0]?.vehicleType || "—",
            addedAt:     new Date().toISOString(),
          },
          ...prev,
        ];
      });
    },
    onError: (err) => {
      console.error("Failed to add tracked vehicle:", err.message);
    },
  });
}

export function useBackfillVendorVehicles() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post("/api/fleet/fastag/backfill-vendors");
      return data;
    },
    onSuccess: (data) => {
      const delay = Math.min(8000 + (data.total || 0) * 200, 30000);
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: KEYS.trackedVehicles() });
        queryClient.invalidateQueries({ queryKey: ["fastag", "db", "all"] });
      }, delay);
    },
  });
}

export function useClassifyVehicles() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post("/api/fleet/fastag/classify-vehicles");
      return data;
    },
    onSuccess: (data) => {
      const delay = Math.min(8000 + (data.total || 0) * 200, 30000);
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: KEYS.trackedVehicles() });
      }, delay);
    },
    onError: (err) => {
      console.error("Classify vehicles failed:", err.message);
    },
  });
}

export function useReconcileVendorCodes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post("/api/fleet/fastag/reconcile-vendor-codes");
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: KEYS.trackedVehicles() });
    },
    onError: (err) => {
      console.error("Reconcile vendor codes failed:", err.message);
    },
  });
}